import { lensPath, view } from "ramda";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { type DocObj, type TopLevelDataObj } from "@hsl/fund-explorer/types";
import dayjs from "@hsl/lgim-explorer/src/lib/dayjs";

import { Translate } from "../../../../../hooks/useTranslation";
import { useFundCardContext } from "../../../../FundCard/context";
import Tooltip from "../../../../Tooltip";
import { DATE_FORMAT } from "../DocumentDisplay";

export const DocElem = ({
    url,
    name,
    asAtDate,
    icon,
    showName,
    showLang,
    type,
    lang,
    shareClass,
    fund,
    onClick,
}: Omit<DocObj, "url"> & {
    icon: JSX.Element;
    showName?: boolean;
    showLang?: boolean;
    type?: string;
    shareClass?: TopLevelDataObj;
    fund?: FundDataWithType;
    url?: string;
    onClick?: () => void;
}) => {
    const { downloadDocumentEvent } = useYardActionEvents();
    const { fund: contextFund } = useFundCardContext(fund);

    const handleDocLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (
        e,
    ) => {
        e.stopPropagation();
        onClick?.();
        const fund = {
            name: contextFund?.data.data.name,
            id: contextFund?.data.id,
            type: contextFund?.data.type,
        };
        const getSCProp = (prop: string): string =>
            view(lensPath(["data", "data", prop]), shareClass) ??
            view(lensPath(["data", prop]), shareClass);
        const code = getSCProp("isin") ?? getSCProp("scopeCode") ?? "";
        const name = getSCProp("shortName") ?? "";
        downloadDocumentEvent({
            file: {
                date: asAtDate ?? "",
                name,
                type: type ?? "",
                url,
            },
            fund,
            shareClass: {
                code,
                name,
            },
        });
    };
    const Link = url ? (
        <a
            href={url}
            title={name}
            target="_blank"
            rel="noreferrer"
            onClick={handleDocLinkClick}
        >
            {icon}
        </a>
    ) : (
        <>{icon}</>
    );

    const Name = url ? (
        <a
            href={url}
            target="_blank"
            className="pl-2"
            onClick={handleDocLinkClick}
            rel="noreferrer"
        >
            <Translate>{name}</Translate>
            <span className="text-xs italic opacity-90">
                {showLang ? ` (${lang})` : ""}
            </span>
        </a>
    ) : (
        <span className="pl-2">
            <Translate>{name}</Translate>
            <span className="text-xs italic opacity-90">
                {showLang ? ` (${lang})` : ""}
            </span>
        </span>
    );

    const TooltipStr = (
        <span>
            {dayjs(asAtDate).format(DATE_FORMAT)}
            <span className="text-xs italic opacity-90">
                {showLang && !!lang && ` (${lang})`}
            </span>
        </span>
    );

    if (asAtDate) {
        return (
            <div className="flex">
                <Tooltip
                    key={url}
                    content={TooltipStr}
                    placement="top"
                    targetClassName="grid place-items-center"
                >
                    {Link}
                </Tooltip>
                {showName && Name}
            </div>
        );
    } else {
        return (
            <div className="flex">
                {Link}
                {showName && Name}
            </div>
        );
    }
};
